import { useCallback, useRef, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import { normalizeId } from '../utils/idNormalization';
import { createCodeCacheUtils } from '../utils/codeCacheUtils';

// Define query keys for codes
export const codeKeys = {
  all: ['codes'],
  lists: () => [...codeKeys.all, 'list'],
  list: (projectId) => [...codeKeys.lists(), normalizeId(projectId)],
  details: () => [...codeKeys.all, 'detail'],
  detail: (id) => [...codeKeys.details(), normalizeId(id)],
};

/**
 * Hook to fetch all codes for a project and store them individually in the cache
 * @param {string|number} projectId - The ID of the project to fetch codes for
 * @returns {Object} The query result containing codes data, loading state, and error
 */
export function useCodes(projectId) {
  const queryClient = useQueryClient();
  const normalizedProjectId = normalizeId(projectId);
  const codeCacheUtils = createCodeCacheUtils(queryClient);

  return useQuery({
    queryKey: codeKeys.list(normalizedProjectId),
    queryFn: () => QualAPI.getCodes(projectId)
      .then(response => {
        // Store each code individually in the cache for later retrieval
        if (response.data && response.data.codes) {
          // Clean up stale code detail queries
          codeCacheUtils.cleanupStaleCodeQueries(response.data.codes);

          // Update cache with current codes
          response.data.codes.forEach(code => {
            const normalizedCodeId = normalizeId(code.id);
            queryClient.setQueryData(
              codeKeys.detail(normalizedCodeId),
              code
            );
          });
        }
        return response.data;
      }),
    enabled: !!projectId,
    retry: (failureCount, error) => {
      // Don't retry on 404 or 403 errors
      if (error?.response?.status === 404 ||
          error?.response?.status === 403 ||
          error?.status === 404 ||
          error?.status === 403) {
        return false;
      }

      // For other errors, retry up to 3 times
      return failureCount < 3;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });
}

// Query-cache events about observers rather than data. A useQuery mounting,
// unmounting, recomputing its own result, or re-rendering with a new options
// object never touches query.state.data, so cache readers have nothing to
// re-read on them.
const OBSERVER_EVENT_TYPES = new Set([
  'observerAdded',
  'observerRemoved',
  'observerOptionsUpdated',
  'observerResultsUpdated',
]);

// Whether a query-cache event can change what the codes readers below see.
//
// Pages mount hundreds of code readers (one per code tag / coded
// sub-element), and every event that reaches a reader costs it a snapshot
// read, so the subscription must be narrow:
// - the cache emits an event for every query add/update/remove anywhere in
//   the app (excerpt fetches, mutations, ...): only 'codes' keys matter;
// - observer bookkeeping events are dropped. react-query emits
//   observerOptionsUpdated on every re-render of every useQuery (its
//   queryKey is a fresh array each render), and each snippet card holds a
//   disabled codes observer (CodeMultiSelect -> useCodesSidebar), so without
//   this a page-wide re-render cost (snippet cards) x (code tags) snapshot
//   reads: the 2026-09 "selecting a snippet freezes the page" regression.
// Events without a query are passed through as the conservative default.
export function isCodesDataEvent(event) {
  if (!event) return true;
  if (OBSERVER_EVENT_TYPES.has(event.type)) return false;
  const key = event.query?.queryKey;
  return !key || key[0] === 'codes';
}

function subscribeToCodesCache(queryClient, onStoreChange) {
  return queryClient.getQueryCache().subscribe((event) => {
    if (isCodesDataEvent(event)) onStoreChange();
  });
}

// Find a code in any cached codes list. Fallback for readers when the
// detail entry is missing (e.g. garbage-collected while a list survives).
function findCodeInLists(queryClient, normalizedCodeId) {
  const codesQueries = queryClient.getQueriesData({ queryKey: codeKeys.lists() });
  for (const [, codesData] of codesQueries) {
    if (codesData?.codes) {
      const code = codesData.codes.find(c =>
        normalizeId(c.id) === normalizedCodeId
      );

      if (code) return code;
    }
  }

  return undefined;
}

/**
 * Hook to read code data by ID from the cache (with fallback to useCodes cache).
 *
 * Pure cache read: it never fetches and is deliberately NOT a useQuery
 * observer. It reads codeKeys.detail(id), which useCodeDetails fetches, and
 * any extra observer on that key — even a disabled one — feeds its own
 * options into the shared query: the previous cache-only useQuery version
 * suppressed useCodeDetails' retries, so a failed GET /api/codes/:id never
 * retried and never reached error state.
 *
 * Because readers are not observers, nothing here pins cache entries against
 * garbage collection — createQueryClient sets a 30-minute gcTime default for
 * all codes queries so entries outlive the reader-only pages.
 * @param {string|number} codeId - The ID of the code to read
 * @returns {Object} { data } — the code from the detail cache, falling back
 *   to any loaded codes list
 */
export function useCode(codeId) {
  const normalizedCodeId = normalizeId(codeId);
  const queryClient = useQueryClient();

  const subscribe = useCallback(
    (onStoreChange) => subscribeToCodesCache(queryClient, onStoreChange),
    [queryClient]
  );

  // Both branches return objects held in the cache, so the snapshot is
  // referentially stable until the underlying cache entry actually changes.
  const getSnapshot = useCallback(() => {
    if (!normalizedCodeId) return undefined;

    const cached = queryClient.getQueryData(codeKeys.detail(normalizedCodeId));
    if (cached) return cached;

    return findCodeInLists(queryClient, normalizedCodeId);
  }, [queryClient, normalizedCodeId]);

  const data = useSyncExternalStore(subscribe, getSnapshot);

  return { data };
}

/**
 * Hook to read multiple codes by ID from the cache (with fallback to the
 * useCodes cache, like useCode).
 *
 * Pure cache read — like useCode, deliberately NOT a query observer: extra
 * observers on codeKeys.detail(id) bleed their options (retry: false) into
 * the query useCodeDetails fetches, suppressing its retries and error state.
 * @param {Array<string|number>} codeIds - Array of code IDs to read
 * @returns {Object} { codes } — codes found in the cache, in input order
 */
export function useCodesById(codeIds = []) {
  const queryClient = useQueryClient();
  // Stable identity for the id list: callers pass fresh arrays each render.
  const idsKey = codeIds.map(id => normalizeId(id)).join(',');

  const subscribe = useCallback(
    (onStoreChange) => subscribeToCodesCache(queryClient, onStoreChange),
    [queryClient]
  );

  // useSyncExternalStore requires a referentially stable snapshot, so reuse
  // the previous array whenever every element is unchanged.
  const lastSnapshotRef = useRef({ idsKey: null, codes: [] });
  const getSnapshot = useCallback(() => {
    const ids = idsKey ? idsKey.split(',').map(normalizeId) : [];
    const codes = [];
    for (const id of ids) {
      const code = queryClient.getQueryData(codeKeys.detail(id)) ||
        findCodeInLists(queryClient, id);
      if (code) codes.push(code);
    }

    const prev = lastSnapshotRef.current;
    const unchanged =
      prev.idsKey === idsKey &&
      prev.codes.length === codes.length &&
      prev.codes.every((code, i) => code === codes[i]);
    if (unchanged) return prev.codes;

    lastSnapshotRef.current = { idsKey, codes };
    return codes;
  }, [queryClient, idsKey]);

  const codes = useSyncExternalStore(subscribe, getSnapshot);

  return { codes };
}

// Simple utility function to get all child IDs recursively
function getAllChildCodeIds(codes, parentId) {
  const normalizedParentId = normalizeId(parentId);
  const childIds = [];

  // Find direct children
  const directChildren = codes.filter(code =>
    normalizeId(code.parent_id) === normalizedParentId
  );

  // For each direct child, get its ID and recursively get its children
  directChildren.forEach(child => {
    const childId = normalizeId(child.id);
    childIds.push(childId);

    // Recursively get children of this child
    const grandchildren = getAllChildCodeIds(codes, childId);
    childIds.push(...grandchildren);
  });

  return childIds;
}

/**
 * Retrieves child code IDs from the react query cache recursively.
 * @param {*} projectId
 * @param {*} parentCodeId
 * @returns {Array[string]} An array of child code IDs for the given parent code ID
 */
export function useChildCodeIdsFromCache(projectId, parentCodeId) {
  const queryClient = useQueryClient();
  const codesData = queryClient.getQueryData(codeKeys.list(normalizeId(projectId)));

  if (!codesData?.codes || !parentCodeId) {
    return [];
  }

  return getAllChildCodeIds(codesData.codes, parentCodeId);
}

export function useCodeDetails(codeId, options = {}) {
  const queryClient = useQueryClient();
  const normalizedCodeId = normalizeId(codeId);
  const cacheUtils = createCodeCacheUtils(queryClient);

  // Query for fetching code details
  const query = useQuery({
    queryKey: codeKeys.detail(normalizedCodeId),
    queryFn: () => {
      return QualAPI.getCode(normalizedCodeId).then((response) => {
        // Update cache when we successfully fetch code details
        // (v5 removed useQuery onSuccess)
        cacheUtils.updateCode(normalizedCodeId, response.data);
        return response.data;
      });
    },
    enabled: !!codeId,
    ...options
  });

  return query;
}
